/**
 * `returnUrl` round-trip for the anon -> /auth/login -> AAD -> deep-link flow.
 *
 * `encodeReturnUrl` packs the caller's current location into a
 * `?returnUrl=...` query-string fragment. The encoded value is
 * `encodeURIComponent(pathname + search + hash)` — hash is optional but
 * harmless to preserve, and routing libraries that scroll-anchor on
 * `#section` benefit from it surviving the bounce.
 *
 * `safeDecodeReturnUrl` is the defensive parser the consuming surface
 * (LoginPage today; `signIn(returnUrl?)` after Group 4) feeds into the
 * AAD `post_login_redirect_uri` round-trip. It MUST reject every open-
 * redirect shape — protocol-relative URLs, percent-encoded slashes, mixed
 * separators, and `javascript:` schemes are the dangerous ones. Returns
 * `undefined` on any rejection so the caller falls back to `/dashboard`.
 *
 * Validation strategy:
 *   1. `decodeURIComponent` first — percent-encoded payloads are the
 *      attacker's primary bypass (e.g. `%2F%2Fevil.com`).
 *   2. Reject if decoded value contains control chars (`U+0000` ..
 *      `U+001F`, `U+007F`) — header-injection / response-splitting vector.
 *      Implemented as an explicit charCode check to avoid a regex literal
 *      containing control characters (Biome `noControlCharactersInRegex`).
 *   3. Require a leading slash that is NOT followed by another slash or
 *      a backslash. The regex `/^\/(?!\/|\\)/` matches `/foo`, `/`, but
 *      rejects `//evil.com` and `/\evil.com`.
 *
 * Everything else (absolute URLs, `javascript:`, empty string, null /
 * undefined) is implicitly rejected by the leading-slash check.
 */

export interface LocationLike {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

export function encodeReturnUrl(location: LocationLike): string {
  const composed = `${location.pathname}${location.search}${location.hash}`;
  return encodeURIComponent(composed);
}

// Leading `/` but NOT followed by `/` or `\` — blocks `//evil.com` and
// `/\evil.com` (browsers normalise the latter to a protocol-relative URL).
const SAFE_RELATIVE_PATH = /^\/(?!\/|\\)/;

function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    // C0 controls (0x00..0x1F) + DEL (0x7F). Both are valid header-
    // injection / response-splitting payloads and have no business in a
    // same-origin relative path.
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

export function safeDecodeReturnUrl(raw: string | null | undefined): string | undefined {
  if (raw === null || raw === undefined || raw === '') return undefined;
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return undefined;
  }
  if (hasControlChars(decoded)) return undefined;
  if (!SAFE_RELATIVE_PATH.test(decoded)) return undefined;
  return decoded;
}
